/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { IconPlus } from '@tabler/icons-react'
import { useGetStudyTexts } from '@/services/studyTextService'
import { ModuleItemType } from '@/types/module'
import { PagingInfo } from '@/types/user'
import { useModulesStore } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import BaseContainer from '@/components/ui/base-container'
import { Button } from '@/components/ui/button'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import ImportFilesModal from './components/ImportFilesModal'
import StudyTextTable from './components/StudyTextTable'

const StudyText = () => {
  const { items: modules, setSelectedModule } = useModulesStore()

  const [isOpenImportModal, setIsOpenImportModal] = useState(false)
  const [tab, setTab] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const debouncedQuery = useDebounce(query)

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  const { studyTexts, isLoadingStudyTexts, meta, refetchStudyTexts } =
    useGetStudyTexts({
      page,
      search: debouncedQuery,
      moduleId: Number(tab),
    })

  useEffect(() => {
    if (modules && modules.length > 0) {
      setSelectedModule(modules[0])
      setTab(String(modules[0].id))
    }
  }, [modules])

  return (
    <Main>
      <PageTitle title='Study Text' />
      <div className=''>
        <div className='mb-4 py-1'>
          <ul className='mb-4 flex w-full gap-1 overflow-auto pb-1'>
            {modules?.map((item: ModuleItemType) => {
              const id = String(item?.id ?? '')
              const isActive = tab === id
              return (
                <li key={id}>
                  <button
                    onClick={() => {
                      setTab(item?.id?.toString())
                      setSelectedModule(item)
                      setPage(1)
                    }}
                    className={cn(
                      'group flex w-full items-center gap-3 rounded-[8px] border-[1px] border-[#EAECF0] bg-[#fff] px-4 py-2.5 text-sm capitalize transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0057B8]/30',
                      isActive
                        ? 'rounded-[8px] border-[1px] border-[#0069E0] bg-[#EBF4FF] text-[#0057B8]'
                        : 'text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#003A80]'
                    )}
                    aria-label={item.name}
                  >
                    <span className='truncate text-base'>{item.name}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <BaseContainer
          action={
            <div className='flex w-full items-center justify-between gap-2.5'>
              <p className='text-2xl text-[#002652]'>Study Text</p>

              <Flex gap={'26px'}>
                <SearchBox
                  placeHolder='Search for File Name'
                  onValueChange={(value: string) => setQuery(value)}
                />
                <Button
                  variant='default'
                  className='px-3.5 py-2.5'
                  onClick={() => setIsOpenImportModal(true)}
                >
                  <IconPlus />
                  Import
                </Button>
              </Flex>
            </div>
          }
        >
          {isLoadingStudyTexts ? (
            <div className='flex w-full justify-center py-10'>
              <Spinner size={'3'} />
            </div>
          ) : (
            <StudyTextTable
              meta={meta as PagingInfo}
              data={studyTexts ?? []}
              page={page}
              handlePageChange={setPage}
            />
          )}
        </BaseContainer>
      </div>
      {isOpenImportModal && (
        <ImportFilesModal
          refreshStudyTexts={() => refetchStudyTexts()}
          moduleId={tab ?? ''}
          open={isOpenImportModal}
          onOpenChange={() => setIsOpenImportModal(!isOpenImportModal)}
        />
      )}
    </Main>
  )
}

export default StudyText
